import { EuiEmptyPrompt, EuiPageTemplate, EuiText } from "@inscopix/ideas-eui";
import { Route, Switch, useParams } from "react-router-dom";
import { Page404 } from "pages/404/Page404";
import { Helmet } from "react-helmet-async";
import { useEffect } from "react";
import { pollAnalysisResults } from "./PageProject.helpers";
import { ToolSpecProvider } from "providers/ToolSpecProvider/ToolSpecProvider";
import { ProjectStatus } from "types/constants";
import { PageGenericDataTables } from "pages/gdt/PageGenericDataTables";
import { ProjectPermissionProvider } from "./ProjectPermissionProvider";
import { PageProject404 } from "./PageProject404";
import { ModalProvider } from "providers/ModalProvider/ModalProvider";
import { DataTableProvider } from "pages/gdt/store/DataTableProvider";
import { ProjectFoldersProvider } from "pages/gdt/folders/ProjectFoldersProvider";
import AppLoading from "components/AppLoading/AppLoading";
import { useProject } from "pages/gdt/store/DataTableProvider.api";
import { isNonNullish } from "utils/isNonNullish";

export const PageProject = () => {
  const projectKey = useParams<{ projectKey: string }>().projectKey;
  const tableKey = useParams<{ tableKey: string | undefined }>().tableKey;

  // Poll for analysis results until the component unmounts
  useEffect(() => {
    const stopPolling = pollAnalysisResults(projectKey);
    return stopPolling;
  }, [projectKey]);

  const { isLoading: loading, error, data: project } = useProject(projectKey);

  if (loading && project === undefined) {
    return <AppLoading />;
  }

  if (isNonNullish(error) || project === undefined) {
    return <PageProject404 projectKey={projectKey} />;
  }

  if (project.is_tool_project) {
    return <Page404 />;
  }

  if (project.status === ProjectStatus.CLONING) {
    return (
      <>
        <Helmet>
          <title>{project.name}</title>
        </Helmet>
        <EuiPageTemplate>
          <EuiPageTemplate>
            <EuiEmptyPrompt
              iconType="copy"
              title={
                <EuiText>
                  <h1>Copying in progress</h1>
                </EuiText>
              }
              body={
                <EuiText>
                  <p>
                    The project you are trying to access is in the process of
                    being copied. Please check back later.
                  </p>
                </EuiText>
              }
            />
          </EuiPageTemplate>
        </EuiPageTemplate>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${project.name}`}</title>
      </Helmet>
      <Switch>
        <ToolSpecProvider>
          <ProjectPermissionProvider projectId={project.id}>
            <DataTableProvider projectKey={projectKey} tableKey={tableKey}>
              <ProjectFoldersProvider projectKey={projectKey}>
                <ModalProvider>
                  <PageGenericDataTables />
                </ModalProvider>
              </ProjectFoldersProvider>
            </DataTableProvider>
          </ProjectPermissionProvider>
        </ToolSpecProvider>
        <Route path="*">
          <Page404 />
        </Route>
      </Switch>
    </>
  );
};
